// Small localStorage-backed cache so detail pages opened in new windows
// (from "View details →" links) can render immediately with data the
// overview page already fetched, then revalidate in the background.
//
// Scope: per-origin. TTL default 60s — long enough to cover a user clicking
// through from the overview, short enough that stale data doesn't linger
// if the tab is left open and then revisited.

const PREFIX = 'synops:cache:';
const DEFAULT_TTL_MS = 60_000;

interface CacheEntry<T> {
  data: T;
  ts: number;
}

export function cacheKey(url: string): string {
  return PREFIX + url;
}

export function getCached<T>(url: string, ttlMs: number = DEFAULT_TTL_MS): T | null {
  try {
    const raw = window.localStorage.getItem(cacheKey(url));
    if (!raw) return null;
    const entry: CacheEntry<T> = JSON.parse(raw);
    if (Date.now() - entry.ts > ttlMs) return null;
    return entry.data;
  } catch {
    return null;
  }
}

export function setCache<T>(url: string, data: T): void {
  try {
    const entry: CacheEntry<T> = { data, ts: Date.now() };
    window.localStorage.setItem(cacheKey(url), JSON.stringify(entry));
  } catch {
    // Quota exceeded or disabled — caching is opportunistic, fine to skip.
  }
}

export function clearCache(url: string): void {
  try {
    window.localStorage.removeItem(cacheKey(url));
  } catch {
    /* ignore */
  }
}
